import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { OmniCheckbox } from '@/components/ui/omni-checkbox';
import { ImageWithFallback } from '@/components/figma/ImageWithFallback';
import { SectionTitle } from '@/components/PageHeader';
import { updateTeamMemberBrandSettings, type TeamMemberRow } from '@/api/beatlyApi';
import { getTokenFromStorage } from '@/utils/auth';
import { showError } from '@/utils/alert';
import type { MemberBrandSettingUi } from '@/utils/teamMemberBrandSettings';
import { mapBrandSettingsFromApi } from '@/utils/teamMemberBrandSettings';

type BrandSettingField = 'want_email_notifications' | 'show_campaigns';

export type TeamBrandSettingsMember = {
  id: string;
  name: string;
  email: string;
  status?: string;
  brandSettings?: MemberBrandSettingUi[];
};

export type TeamBrandSettingsBrand = {
  id: string;
  name: string;
  logo: string;
};

export type TeamBrandSettingsSetMembers = React.Dispatch<
  React.SetStateAction<TeamBrandSettingsMember[]>
>;

type TeamBrandSettingsSectionProps = {
  teamId: string | null;
  isAdmin: boolean;
  viewerUserId: string;
  members: TeamBrandSettingsMember[];
  setMembers: TeamBrandSettingsSetMembers;
  brands: TeamBrandSettingsBrand[];
  logout: () => void;
};

export function TeamBrandSettingsSection({
  teamId,
  isAdmin,
  viewerUserId,
  members,
  setMembers,
  brands,
  logout,
}: TeamBrandSettingsSectionProps) {
  const { t } = useTranslation();
  const [loadingCell, setLoadingCell] = useState<string | null>(null);

  if (!teamId) {
    return null;
  }

  const safeTeamId = teamId;

  const membersWithBrandRows = members.filter((m) => {
    if (m.status !== 'MEMBER' || (m.brandSettings?.length ?? 0) === 0) {
      return false;
    }
    if (isAdmin) {
      return true;
    }
    return m.id === viewerUserId;
  });

  function cellKey(memberId: string, brandId: string, field: BrandSettingField) {
    return `${memberId}:${brandId}:${field}`;
  }

  function canEditMember(member: TeamBrandSettingsMember) {
    return viewerUserId === member.id || isAdmin;
  }

  function mergeMemberFromApiRow(row: TeamMemberRow) {
    const rowUserId = String(row.user_id ?? '');
    const nextSettings = mapBrandSettingsFromApi(row.brand_settings);
    setMembers((prev) =>
      prev.map((m) => {
        if (m.id !== rowUserId) {
          return m;
        }
        // PATCH responses may omit `brand_settings` (or send []) depending on serialization;
        // replacing with undefined drops the member from this section entirely.
        return {
          ...m,
          brandSettings: nextSettings ?? m.brandSettings,
        };
      })
    );
  }

  async function handleToggle(
    member: TeamBrandSettingsMember,
    brandId: string,
    field: BrandSettingField,
    checked: boolean
  ) {
    if (!canEditMember(member)) {
      return;
    }
    const token = getTokenFromStorage();
    if (!token) {
      logout();
      return;
    }

    const key = cellKey(member.id, brandId, field);
    setLoadingCell(key);
    try {
      const body =
        field === 'want_email_notifications'
          ? { want_email_notifications: checked }
          : { show_campaigns: checked };
      const updated = await updateTeamMemberBrandSettings(safeTeamId, member.id, brandId, token, body);
      mergeMemberFromApiRow(updated);
    } catch {
      showError(t('teamPage.brandSettings.errorUpdate'));
    } finally {
      setLoadingCell(null);
    }
  }

  const brandLogoById = new Map(brands.map((b) => [b.id, b.logo]));

  return (
    <div className="mb-12">
      <div className="mb-6">
        <SectionTitle title={t('teamPage.brandSettings.title')} />
        <p
          id="team-brand-settings-description"
          className="mt-2 text-sm text-[#667085] max-w-2xl"
        >
          {t(
            isAdmin
              ? 'teamPage.brandSettings.descriptionAdmin'
              : 'teamPage.brandSettings.descriptionSelf'
          )}
        </p>
      </div>

      <section
        aria-label={t('teamPage.brandSettings.title')}
        aria-describedby="team-brand-settings-description"
      >
        {membersWithBrandRows.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 px-5 py-12 text-center text-sm text-[#98a2b3]">
            {t('teamPage.brandSettings.empty')}
          </div>
        ) : (
          <div className="space-y-6">
            {membersWithBrandRows.map((member) => (
              <div key={`brand-settings-${member.id}`} className="space-y-3">
                <p className="text-xs font-semibold text-[#344054] tracking-wide">
                  {t('teamPage.brandSettings.memberLabel', {
                    name: member.name,
                    email: member.email,
                  })}
                </p>

                <div className="overflow-x-auto rounded-2xl border border-gray-100 bg-white">
                  <div className="min-w-[520px]">
                    <div className="grid grid-cols-12 gap-4 px-5 py-3 border-b border-gray-100 bg-[#f8f9fa]/60">
                      <div className="col-span-5">
                        <span
                          className="text-[10px] text-[#98a2b3] tracking-[0.06em] font-semibold uppercase"
                        >
                          {t('teamPage.brandSettings.columnBrand')}
                        </span>
                      </div>
                      <div className="col-span-3 text-center">
                        <span
                          className="text-[10px] text-[#98a2b3] tracking-[0.06em] font-semibold uppercase"
                        >
                          {t('teamPage.brandSettings.columnEmailNotifications')}
                        </span>
                      </div>
                      <div className="col-span-4 text-center">
                        <span
                          className="text-[10px] text-[#98a2b3] tracking-[0.06em] font-semibold uppercase"
                        >
                          {t('teamPage.brandSettings.columnShowCampaigns')}
                        </span>
                      </div>
                    </div>

                    {(member.brandSettings ?? []).map((row, idx, arr) => {
                      const brandLabel = row.name || row.brandId;
                      const editable = canEditMember(member);
                      const emailKey = cellKey(member.id, row.brandId, 'want_email_notifications');
                      const showKey = cellKey(member.id, row.brandId, 'show_campaigns');
                      const logo = brandLogoById.get(row.brandId) ?? '';
                      const isLast = idx === arr.length - 1;

                      return (
                        <div
                          key={`${member.id}-${row.brandId}`}
                          className={`grid grid-cols-12 gap-4 items-center px-5 py-4 border-b border-gray-50 hover:bg-[#f8f9fa]/40 transition-colors ${
                            isLast ? 'border-b-0' : ''
                          }`}
                        >
                          <div className="col-span-5 flex items-center gap-3 min-w-0">
                            <div className="w-8 h-8 rounded-xl overflow-hidden bg-gray-100 shrink-0 border border-gray-100">
                              {logo ? (
                                <ImageWithFallback
                                  src={logo}
                                  alt=""
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-[10px] font-bold text-[#98a2b3]">
                                  {brandLabel.slice(0, 1).toUpperCase()}
                                </div>
                              )}
                            </div>
                            <span className="text-sm text-[#101828] truncate font-medium">{brandLabel}</span>
                          </div>

                          <div className="col-span-3 flex justify-center">
                            <div className="flex items-center justify-center min-h-9">
                              {loadingCell === emailKey ? (
                                <Loader2 className="w-5 h-5 text-[#be43ff] animate-spin" aria-hidden />
                              ) : (
                                <OmniCheckbox
                                  checked={row.wantEmailNotifications}
                                  disabled={!editable}
                                  onCheckedChange={(next) =>
                                    void handleToggle(
                                      member,
                                      row.brandId,
                                      'want_email_notifications',
                                      next
                                    )
                                  }
                                  aria-label={t('teamPage.brandSettings.ariaEmailNotifications', {
                                    brand: brandLabel,
                                  })}
                                />
                              )}
                            </div>
                          </div>

                          <div className="col-span-4 flex justify-center">
                            <div className="flex items-center justify-center min-h-9">
                              {loadingCell === showKey ? (
                                <Loader2 className="w-5 h-5 text-[#be43ff] animate-spin" aria-hidden />
                              ) : (
                                <OmniCheckbox
                                  checked={row.showCampaigns}
                                  disabled={!editable}
                                  onCheckedChange={(next) =>
                                    void handleToggle(member, row.brandId, 'show_campaigns', next)
                                  }
                                  aria-label={t('teamPage.brandSettings.ariaShowCampaigns', {
                                    brand: brandLabel,
                                  })}
                                />
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
